import React from 'react';
import {
  DataTable,
  useListContext,
  useNotify,
  useGetOne,
  BulkDeleteButton,
} from 'react-admin';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Tooltip from '@mui/material/Tooltip';

import { getCurrencySymbol } from '@src/utils/currency';
import { requirePartner } from '@src/providers/dataProvider/shared';
import type { PartnerSettings } from '@src/settings';
import type { PricingRule, PricingType } from './types';
import { updatePricingRulePosition } from './updatePricingRulePosition';

const formatDiscount = (rule: PricingRule, currencySymbol: string): string => {
  const discount = rule.discount ?? 0;
  if (rule.sourceColumn === 'absolute_price') {
    // Convert cents to currency amount
    return `${currencySymbol}${(discount / 100).toFixed(2)}`;
  }
  return `${discount}%`;
};

const PositionControls = ({ rule }: { rule: PricingRule }) => {
  const notify = useNotify();
  // Use refetch from list context instead of global refresh() to avoid
  // refetching unrelated data (settings, facets, etc.) on every position change.
  const { data, refetch } = useListContext<PricingRule>();

  const handleMoveUp = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (rule.position <= 0) return;

    try {
      const partner = requirePartner();
      await updatePricingRulePosition({
        partnerId: partner.id,
        ruleId: rule.id,
        newPosition: rule.position - 1,
      });
      refetch();
    } catch (error) {
      console.error('PricingRulesDataTable: handleMoveUp failed', {
        ruleId: rule.id,
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Failed to move rule up';
      notify(message, { type: 'error' });
    }
  };

  const handleMoveDown = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const partner = requirePartner();
      await updatePricingRulePosition({
        partnerId: partner.id,
        ruleId: rule.id,
        newPosition: rule.position + 1,
      });
      refetch();
    } catch (error) {
      console.error('PricingRulesDataTable: handleMoveDown failed', {
        ruleId: rule.id,
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Failed to move rule down';
      notify(message, { type: 'error' });
    }
  };

  const isFirst = rule.position === 0;
  const isLast = data && data.length > 0 && rule.position === data[data.length - 1].position;

  return (
    <>
      <Tooltip title="Move up">
        <span>
          <IconButton
            size="small"
            onClick={handleMoveUp}
            disabled={isFirst}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Move down">
        <span>
          <IconButton
            size="small"
            onClick={handleMoveDown}
            disabled={isLast}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

interface PricingRulesDataTableProps {
  pricingType: PricingType;
}

const PricingRulesDataTable = ({ pricingType }: PricingRulesDataTableProps) => {
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencySymbol = getCurrencySymbol(settings?.primaryCurrencyCode);

  const isConditionType = pricingType === 'condition';

  return (
    <DataTable
      bulkActionButtons={<BulkDeleteButton />}
      rowClick="edit"
    >
      {isConditionType ? (
        <>
          <DataTable.Col
            source="condition"
            label="Condition"
            disableSort
            render={(record: PricingRule) => record.condition || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="department"
            label="Department"
            disableSort
            render={(record: PricingRule) => record.department || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="category"
            label="Category"
            disableSort
            render={(record: PricingRule) => record.category || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="merchantCategory"
            label="Merchant Category"
            disableSort
            render={(record: PricingRule) => record.merchantCategory || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="tag"
            label="Tag"
            disableSort
            render={(record: PricingRule) => record.tag || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="name"
            label="Name"
            disableSort
            render={(record: PricingRule) => record.name || <em style={{ color: '#888' }}>All</em>}
          />
        </>
      ) : (
        <>
          <DataTable.Col
            source="sourceColumn"
            label="Source Column"
            disableSort
          />
          <DataTable.Col
            source="department"
            label="Department"
            disableSort
            render={(record: PricingRule) => record.department || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="category"
            label="Category"
            disableSort
            render={(record: PricingRule) => record.category || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="merchantCategory"
            label="Merchant Category"
            disableSort
            render={(record: PricingRule) => record.merchantCategory || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="upc"
            label="UPC"
            disableSort
            render={(record: PricingRule) => record.upc || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="tag"
            label="Primary Tag"
            disableSort
            render={(record: PricingRule) => record.tag || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="name"
            label="Primary Name"
            disableSort
            render={(record: PricingRule) => record.name || <em style={{ color: '#888' }}>-</em>}
          />
          <DataTable.Col
            source="secondaryTag"
            label="Secondary Tag"
            disableSort
            render={(record: PricingRule) => record.secondaryTag || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="secondaryName"
            label="Secondary Name"
            disableSort
            render={(record: PricingRule) => record.secondaryName || <em style={{ color: '#888' }}>-</em>}
          />
          <DataTable.Col
            source="tertiaryTag"
            label="Tertiary Tag"
            disableSort
            render={(record: PricingRule) => record.tertiaryTag || <em style={{ color: '#888' }}>All</em>}
          />
          <DataTable.Col
            source="tertiaryName"
            label="Tertiary Name"
            disableSort
            render={(record: PricingRule) => record.tertiaryName || <em style={{ color: '#888' }}>-</em>}
          />
        </>
      )}
      <DataTable.Col
        source="discount"
        label={isConditionType ? 'Discount (%)' : 'Discount / Price'}
        disableSort
        render={(record: PricingRule) => isConditionType ? `${record.discount ?? 0}%` : formatDiscount(record, currencySymbol)}
      />
      <DataTable.Col
        source="position"
        label="Position"
        disableSort
        sx={{ width: '120px' }}
        render={(record: PricingRule) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {record.position}
            <PositionControls rule={record} />
          </span>
        )}
      />
    </DataTable>
  );
};

export default PricingRulesDataTable;
